import {
  Box,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { organizationRepository } from 'api/organizationRepository';
import InputField from 'components/fields/InputField';
import { useAuth } from 'contexts/authContext';
import { useEffect, useState } from 'react';
import {
  flattenZodErrors,
  organizationSchema,
} from 'validations/Validations';
import { z } from 'zod';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organization: any;
  onUpdated?: () => void;
  showCenteredAlert: (
    msg: string,
    status?: 'success' | 'info' | 'warning' | 'error'
  ) => void;
};

export default function EditOrganizationModal({
  isOpen,
  onClose,
  organization,
  onUpdated,
  showCenteredAlert,
}: Props) {

  const { role } = useAuth();
  const [editOrg, setEditOrg] = useState({
    name: organization?.name || '',
    email: organization?.email || '',
    contact: organization?.contact || '',
    gsTin: organization?.gsTin || '',
    status: organization?.isActive ? 'Active' : 'Inactive',
    hasAMC: organization?.hasAMC || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    setEditOrg({
      name: organization?.name || '',
      email: organization?.email || '',
      contact: organization?.contact || '',
      gsTin: organization?.gsTin || '',
      status: organization?.isActive ? 'Active' : 'Inactive',
      hasAMC: organization?.hasAMC || false,
    });
    setErrors({});
  }, [organization, isOpen]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditOrg(prev => ({
      ...prev,
      hasAMC: e.target.checked,
    }));
  };

  const handleChange = (field: string, value: string) => {
    setEditOrg((prev) => ({ ...prev, [field]: value }));

    const fieldSchema = (organizationSchema.shape as Record<string, z.ZodTypeAny>)[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleUpdateOrganization = async () => {
    try {
      organizationSchema.parse(editOrg);
      setErrors({});

      // if (!organization?.id) {
      //   showCenteredAlert('Organization ID missing.', 'error');
      //   return;
      // }

      setIsUpdating(true);
      const payload = {
        ...organization,
        ...editOrg,
        isActive: editOrg.status === 'Active',
        hasAMC: editOrg.hasAMC,
      };

      await organizationRepository.updateOrganization(
        organization.id,
        payload
      );

      const refreshedOrganization = await organizationRepository.getOrganizationById(organization.id);
      if (onUpdated && refreshedOrganization) onUpdated();

      showCenteredAlert('Organization updated successfully', 'success');
      if
        (onClose)
        onClose();
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(flattenZodErrors(err));
        showCenteredAlert('Please fix the validation errors.', 'warning');
      } else {
        showCenteredAlert('Failed to update organization', 'error');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box bg={cardBg} color={textColor}>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader>Edit Organization Info</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <VStack spacing={2} align="stretch">
              {/* Name */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Organization Name
                </Text>
                <InputField
                  placeholder="Organization Name"
                  value={editOrg.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                  mb={1}
                  fontSize="sm"
                />
                {errors.name && (
                  <Text fontSize="xs" color="red.500">
                    {errors.name}
                  </Text>
                )}
              </Box>

              {/* Email */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Email
                </Text>
                <InputField
                  placeholder="Email"
                  value={editOrg.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
                  mb={1}
                  fontSize="sm"
                />
                {errors.email && (
                  <Text fontSize="xs" color="red.500">
                    {errors.email}
                  </Text>
                )}
              </Box>

              {/* Contact */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Contact
                </Text>
                <InputField
                  placeholder="Contact"
                  value={editOrg.contact}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('contact', e.target.value)}
                  mb={1}
                  fontSize="sm"
                />
                {errors.contact && (
                  <Text fontSize="xs" color="red.500">
                    {errors.contact}
                  </Text>
                )}
              </Box>

              {/* GSTIN */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  GSTIN
                </Text>
                <InputField
                  placeholder="GSTIN"
                  value={editOrg.gsTin}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('gsTin', e.target.value)}
                  mb={1}
                  fontSize="sm"
                />
                {errors.gsTin && (
                  <Text fontSize="xs" color="red.500">
                    {errors.gsTin}
                  </Text>
                )}
              </Box>

              {/* Status */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Status
                </Text>
                <Select
                  placeholder="Select Status"
                  value={editOrg.status}
                  fontSize="sm"
                  onChange={(e) => handleChange('status', e.target.value)}
                  mb={1}
                  bg={inputBg}
                  color={editOrg.status ? textColor : placeholderColor}
                >
                  <option
                    value="Active"
                    style={{ backgroundColor: menuListBg, color: textColor }}
                  >
                    Active
                  </option>
                  <option
                    value="Inactive"
                    style={{ backgroundColor: menuListBg, color: textColor }}
                  >
                    Inactive
                  </option>
                </Select>
                {errors.status && (
                  <Text fontSize="xs" color="red.500">
                    {errors.status}
                  </Text>
                )}
              </Box>
              {role === "Admin" && (
                <Checkbox
                  isChecked={editOrg.hasAMC}
                  onChange={handleCheckboxChange}
                >
                  Is AMC required for this Organization?
                </Checkbox>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Button
              onClick={handleUpdateOrganization}
              colorScheme="blue"
              mr={3}
              isLoading={isUpdating}
            >
              Update
            </Button>
            <Button variant="outline" borderColor="gray.300" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
